/*  iceberg-ui  */
import { Button, ButtonProps } from '@auviknetworks/iceberg-ui';

/* React */
import React from 'react';
import { FieldValues, useFormContext } from 'react-hook-form';

/**
 * It's a temporary replacement until we fix the Iceberg buttons
 *
 * FormModalButton component for use within a form context.
 *
 * This component extends the basic Button functionality with form-specific features,
 * such as disabling the button during form submission.
 *
 * @template TFormValues - The type of the form values, extending FieldValues
 *
 * @param {Object} props - The component props
 * @param {string} [props.data-trackid] - Data attribute for tracking
 * @param {React.ReactNode} props.children - The content to be rendered inside the button
 * @param {string} [props.variant='primary'] - The visual variant of the button
 * @param {string} [props.type='button'] - The type of the button (e.g., 'submit', 'button')
 * @param {Function} [props.onClick] - The function to be called when the button is clicked
 *
 * @returns {JSX.Element} A Button component with form context awareness
 */
export const FormModalButton = <TFormValues extends FieldValues = FieldValues>({
    'data-trackid': dataTrackid = 'formModalButton',
    children,
    variant,
    type,
    onClick
}: ButtonProps) => {
    const { formState } = useFormContext<TFormValues>();

    return (
        <Button
            data-trackid={dataTrackid}
            variant={variant || 'primary'}
            type={type || 'button'}
            onClick={onClick}
            disabled={formState.isSubmitting}
            loading={formState.isSubmitting}
            aria-label={children as string}
            aria-busy={formState.isSubmitting}
            sx={{
                minWidth: '140px',
                px: '20px',
                py: '10px'
            }}
        >
            {children}
        </Button>
    );
};
